// frontend/src/components/AdminRoute.js
import { useEffect, useState } from "react";
import { onAuthStateChange, getSession, checkSiteAdminPermission, signOut } from "@/lib/auth";
import LoginPage from "@/components/LoginPage";
import SetPasswordPage from "@/components/SetPasswordPage";

function AdminRoute({ children }) {
  const [authState, setAuthState] = useState("loading"); // loading | unauthenticated | checking | denied | authorized | recovery
  const [session, setSession] = useState(null);

  useEffect(() => {
    getSession().then((sess) => {
      if (sess) {
        setSession(sess);
        setAuthState((prev) => (prev === "authorized" || prev === "recovery") ? prev : "checking");
      } else {
        setAuthState((prev) => (prev === "authorized" || prev === "recovery") ? prev : "unauthenticated");
      }
    });

    const { data: { subscription } } = onAuthStateChange((event, sess) => {
      if (event === "TOKEN_REFRESHED") {
        setSession(sess);
        return;
      }
      if (event === "PASSWORD_RECOVERY") {
        setSession(sess);
        setAuthState("recovery");
        return;
      }
      setSession(sess);
      if (sess) {
        setAuthState((prev) => (prev === "authorized" || prev === "recovery") ? prev : "checking");
      } else {
        setAuthState("unauthenticated");
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (authState !== "checking" || !session) return;
    let cancelled = false;
    checkSiteAdminPermission(session.user.id)
      .then((hasPermission) => {
        if (!cancelled) setAuthState(hasPermission ? "authorized" : "denied");
      })
      .catch((err) => {
        console.error("Failed to check admin permission:", err);
        if (!cancelled) setAuthState("denied");
      });
    return () => { cancelled = true; };
  }, [authState, session]);

  if (authState === "loading" || authState === "checking") {
    return (
      <div className="min-h-screen bg-[#F9F5EE] flex items-center justify-center">
        <p className="text-[#3A3530]">Checking access...</p>
      </div>
    );
  }

  if (authState === "unauthenticated") {
    return <LoginPage />;
  }

  if (authState === "recovery") {
    return (
      <SetPasswordPage
        onComplete={() => setAuthState("checking")}
      />
    );
  }

  if (authState === "denied") {
    return (
      <div className="min-h-screen bg-[#F9F5EE] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h1 className="text-2xl text-[#3A3530] mb-4" style={{ fontFamily: "Federo, serif" }}>
            Access Denied
          </h1>
          <p className="text-[#3A3530] mb-6">
            You don't have the <strong>site-admin</strong> role for this entity. Contact your administrator.
          </p>
          <button
            onClick={() => signOut()}
            className="bg-[#1E1E1E] text-[#F5EDE0] px-4 py-2 text-sm leadline-border-thin"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  if (authState === "authorized") {
    return children;
  }

  // Deny by default — any unhandled auth state shows loading
  return (
    <div className="min-h-screen bg-[#F9F5EE] flex items-center justify-center">
      <p className="text-[#3A3530]">Checking access...</p>
    </div>
  );
}

export default AdminRoute;
